import React from 'react';
import { WifiOff, CloudOff } from 'lucide-react';
import { useOffline } from '../contexts/OfflineContext';

export const OfflineIndicator = () => {
  const { isOnline } = useOffline();

  if (isOnline) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-2 animate-fade-in"
      role="alert"
      aria-live="polite"
      data-testid="offline-indicator"
    >
      <WifiOff className="w-4 h-4 text-amber-600" />
      <span className="text-sm text-amber-800">
        You&apos;re offline — browsing cached data only
      </span>
    </div>
  );
};

// Wrapper component that disables interactions when offline
export const OfflineDisabled = ({ children, fallback = null, showMessage = true }) => {
  const { isOnline } = useOffline();

  if (isOnline) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  if (showMessage) {
    return (
      <div className="opacity-50 pointer-events-none relative">
        {children}
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-lg">
          <div className="flex items-center gap-2 text-muted-foreground text-sm px-3 py-2 bg-background rounded-full border">
            <CloudOff className="w-4 h-4" />
            <span>Available online</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="opacity-50 pointer-events-none">
      {children}
    </div>
  );
};

// Hook to check if an action should be disabled offline
export const useOfflineDisabled = () => {
  const { isOnline } = useOffline();
  
  const isDisabled = !isOnline;
  
  const getOfflineMessage = () => {
    if (isOnline) return null;
    return 'This action requires an internet connection';
  };

  return { isDisabled, isOnline, getOfflineMessage };
};

export default OfflineIndicator;
